import { Injectable } from '@angular/core';
import { RemoteConfig, fetchAndActivate, getStringChanges } from '@angular/fire/remote-config';
import { Observable, combineLatest, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';

/** See which flags we have in firebase https://console.firebase.google.com/project/me-fit-d7a18/config */
const featureNameMap = {
  'homeScreenSettings': 'homeScreenSettingsOrgIds',
  'workoutListBulkActions': 'workoutListBulkActionsOrgIds',
  'tabletTrainingMode': 'tabletTrainingModeOrgIds',
  'whatsappInviteFlow': 'whatsappInviteFlowOrgIds',
  'formsEnabledOrgIds': 'formsEnabledOrgIds',
  'copilotPaneEnabled': 'copilotPaneEnabledOrgIds',
  'medicalEnabledOrgIds': 'medicalEnabledOrgIds'
} as const;

@Injectable({
  providedIn: 'root',
})
export class FeatureFlipService {

  calendarEnabled$ = of(true);

  formsEnabled$ = this.isFeatureEnabled('formsEnabledOrgIds');

  copilotPaneEnabled$ = combineLatest([
    this.$auth.isSuperAdmin$,
    this.isFeatureEnabled('copilotPaneEnabled')
  ]).pipe(
    map(([isSuperAdmin, isEnabled]) => isSuperAdmin || isEnabled)
  );

  medicalActivated$ = combineLatest([
    this.$auth.organization$.pipe(
      map( org => org?.isRoot)
    ),
    this.isFeatureEnabled('medicalEnabledOrgIds')
  ]).pipe(
    map(([isRoot, isEnabled]) => isRoot || isEnabled)
  );

  constructor(private $auth: AuthService, private remoteConfig: RemoteConfig) {
    this.configureRemoteConfig();
  }

  private configureRemoteConfig() {
    this.remoteConfig.defaultConfig = environment.firebaseRemoteConfig.defaultConfig;

    // 12 hours, change to a lower value during development
    this.remoteConfig.settings.minimumFetchIntervalMillis = environment.firebaseRemoteConfig.minimumFetchIntervalMillis;

    fetchAndActivate(this.remoteConfig);
  }

  isFeatureEnabled(featureName: keyof typeof featureNameMap): Observable<boolean> {
    if (!environment.production) {
      // Enable all flags on dev.
      return of(true);
    }

    return combineLatest([
      this.$auth.organization$,
      getStringChanges(this.remoteConfig, featureNameMap[featureName]).pipe(
        map(stringValue => {
          let orgIds: string[] = [];
          try {
            orgIds = JSON.parse(stringValue)
          } catch {}

          return orgIds;
        }),
        tap((homeScreenSettingsOrgIds) => console.log(`[isFeatureEnabled] remote config ${featureName}`, homeScreenSettingsOrgIds)),
      )
    ]).pipe(
      map(([organization, orgIds]) => {
        if (!organization) {
          return false;
        }

        if (organization.isRoot) {
          return true;
        }

        return orgIds.includes(organization.id);
      }
    ));
  }
}
